import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { Surface } from "@/components/common";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Send, Loader2, Check, Undo2 } from "lucide-react";
import { toast } from "sonner";

export function RefinePanel({ result, setResult, module, projectId, quick = [], subtitle, language = "Dansk" }) {
  const { t } = useLocale();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  // Reset conversation + version history when a brand new base is generated
  useEffect(() => { setMessages([]); setHistory([]); }, [projectId]);

  const refine = async (instr) => {
    const value = (typeof instr === "string" ? instr : input).trim();
    if (!value || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: value }]);
    setBusy(true);
    try {
      const { data } = await api.post("/refine", { module, current: result, instruction: value, project_id: projectId, language });
      setHistory((h) => [...h, result]);
      setResult(data.result);
      setMessages((m) => [...m, { role: "assistant", text: t("Opdateret efter dit ønske.") }]);
    } catch (err) {
      toast.error(apiError(err));
      setMessages((m) => [...m, { role: "assistant", text: t("Beklager — det gik ikke. Prøv igen.") }]);
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (history.length === 0 || busy) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setResult(prev);
    setMessages((m) => [...m, { role: "assistant", text: t("Gik tilbage til forrige version.") }]);
    if (projectId) {
      try { await api.put(`/projects/${projectId}`, { content: prev }); } catch {}
    }
  };

  return (
    <Surface testid="refine-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-display font-medium">{t("Juster teksten")}</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={undo} disabled={history.length === 0 || busy} className="gap-2" data-testid="refine-undo-btn">
          <Undo2 className="h-3.5 w-3.5" /> {t("Fortryd")}{history.length > 0 ? ` (${history.length})` : ""}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{subtitle ? t(subtitle) : t("Har du ønsker til denne version? Skriv dem her — så retter Aicom AI teksten til.")}</p>

      {messages.length > 0 && (
        <div className="space-y-2 mb-4 max-h-56 overflow-y-auto" data-testid="refine-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`text-sm px-3 py-2 rounded-lg max-w-[85%] ${m.role === "user" ? "bg-foreground text-background" : "bg-secondary text-muted-foreground flex items-center gap-2"}`}>
                {m.role === "assistant" && <Check className="h-3.5 w-3.5 shrink-0" />} {m.text}
              </div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="text-sm px-3 py-2 rounded-lg bg-secondary text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("Retter teksten…")}</div></div>}
        </div>
      )}

      {quick.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {quick.map((q) => (
            <button key={q} onClick={() => refine(q)} disabled={busy} data-testid={`refine-quick-${q}`}
              className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/50 transition-colors disabled:opacity-50">
              {q}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} disabled={busy}
          onKeyDown={(e) => { if (e.key === "Enter") refine(); }}
          placeholder={t("Skriv dit ønske til justering…")} data-testid="refine-input" />
        <Button onClick={() => refine()} disabled={busy || !input.trim()} className="gap-2 shrink-0" data-testid="refine-send-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("Send")}
        </Button>
      </div>
    </Surface>
  );
}
